import {useMemo} from 'react'

// Hooks
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {SIZE_REFINEMENTS_FALLBACK} from '../constants'
import {categoryUrlBuilder} from '../utils/url'

export const useRefinements = ({refinements = [], sizeGrouping, category}) => {
    const params = useParams()
    const navigate = useNavigation()
    const history = useHistory()
    const location = useLocation()
    const {locale} = useMultiSite()
    const urlParams = new URLSearchParams(location.search)
    const searchQuery = urlParams.get('q')
    const isSearch = !!searchQuery
    const isIntlLocale = locale?.isSupportedByESW
    const [searchParams, {stringify: stringifySearchParams}] = useSearchParams()
    const sizeAttributeIds = sizeGrouping ? sizeGrouping.split(',') : SIZE_REFINEMENTS_FALLBACK
    const grouped = useMemo(() => {
        const grouped = {
            'All Filters': [],
            Size: [],
            Colour: [],
            Price: []
        }

        refinements.forEach((refinement) => {
            if (refinement?.values?.some((value) => value.hitCount > 0)) {
                switch (refinement.attributeId) {
                    case 'price':
                        grouped.Price.push(refinement)
                        break
                    case 'c_refinementColor':
                        grouped.Colour.push(refinement)
                        break
                    default:
                        if (sizeAttributeIds.includes(refinement.attributeId)) {
                            grouped.Size.push(refinement)
                        }
                        break
                }
                grouped['All Filters'].push(refinement)
            }
        })

        Object.keys(grouped).forEach((key) => {
            if (grouped[key].length === 0) {
                delete grouped[key]
            }
        })
        return grouped
    }, [refinements, sizeAttributeIds])

    return {
        groupedRefinements: grouped,
        selectedFilters: searchParams.refine,
        toggleFilter(value, attributeId, selected, allowMultiple = true) {
            const searchParamsCopy = {...searchParams}

            // Remove the `offset` search param if present.
            delete searchParamsCopy.offset

            // Remove the page params for the case you come back from PDP and change the filters
            delete searchParamsCopy.page

            // If we aren't allowing for multiple selections, simply clear any value set for the
            // attribute, and apply a new one if required.
            if (!allowMultiple) {
                const previousValue = searchParamsCopy.refine[attributeId]
                delete searchParamsCopy.refine[attributeId]

                // Note the loose comparison, for "string != number" checks.
                if (!selected && value.value != previousValue) {
                    searchParamsCopy.refine[attributeId] = value.value
                }
            } else {
                // Get the attibute value as an array.
                let attributeValue = searchParamsCopy.refine[attributeId] || []

                // Ensure that the value is still converted into an array if it's a `string` or `number`.
                if (typeof attributeValue === 'string') {
                    attributeValue = attributeValue.split('|')
                } else if (typeof attributeValue === 'number') {
                    attributeValue = [attributeValue]
                }

                // Either set the value, or filter the value out.
                if (!selected) {
                    attributeValue.push(value.value)
                } else {
                    // Note the loose comparison, for "string != number" checks.
                    attributeValue = attributeValue?.filter((v) => v != value.value)
                }

                // Update the attribute value in the new search params.
                searchParamsCopy.refine[attributeId] = attributeValue

                // If the update value is an empty array, remove the current attribute key.
                if (searchParamsCopy.refine[attributeId].length === 0) {
                    delete searchParamsCopy.refine[attributeId]
                }
            }

            if (isSearch) {
                const searchUrl = `/search?${stringifySearchParams(searchParamsCopy)}`
                isIntlLocale ? navigate(searchUrl, 'replace') : navigate(searchUrl)
            } else {
                const categoryUrl = category
                    ? categoryUrlBuilder(category)
                    : `/category/${params.categoryId}`
                const refinementUrl = `${categoryUrl}?${stringifySearchParams(searchParamsCopy)}`
                isIntlLocale ? navigate(refinementUrl, 'replace') : history.replace(refinementUrl)
            }
        }
    }
}
